import * as React from "react";
import cx from "classnames";
import CompactCard from "common/components/compactCard";
import InfiniteScroller from "@vingle/wtf-sdk/components/infiniteScroller";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { parseForSummary } from "@vingle/wtf-sdk/helpers/postContentParser";
import getFirstMedia from "@vingle/wtf-sdk/helpers/getFirstMedia";
import getMediaUrl from "@vingle/wtf-sdk/helpers/getMediaUrl";
import { IUseTrackTicketOption } from "@vingle/wtf-sdk/helpers/trackTicket";

// styles
const styles = require("./compactList.scss");

interface IProps extends React.HTMLProps<HTMLDivElement> {
  cards: ReadonlyArray<Vingle.NewsFeed.IItem> | null;
  handleInfinityLoad?: () => void;
  loader?: React.ReactNode;
  emptyElement?: React.ReactNode;
  paging?: Vingle.IPaging;
  isLoading?: boolean;
  disableModal?: boolean;
  trackTicket?: Omit<IUseTrackTicketOption, "contentId">;
}

// tslint:disable-next-line:no-empty
const placeholderLoad = () => {};

const CompactCardItem = React.memo(
  ({
    card,
    disableModal,
    position,
    trackTicket,
  }: {
    card: Vingle.NewsFeed.IItem;
    position: number;
  } & Pick<IProps, "disableModal" | "trackTicket">) => {
    const { medias, texts } = React.useMemo(
      () => parseForSummary(card.data.content),
      [card.data.content],
    );
    const media = getFirstMedia(medias);
    return (
      <CompactCard
        key={`cardItem_${card.id}`}
        link={new VingleURL.PostShow({
          cardId: card.data.id,
        }).toLocation({
          modal: !disableModal,
          withBrowserSearch: true,
        })}
        trackTicket={
          trackTicket && {
            ...trackTicket,
            contentId: card.data.id,
            position,
          }
        }
        title={card.data.title}
        content={texts.join(" ")}
        media={
          media && {
            type: media.type === "video" ? media.originalType : media.type,
            src: getMediaUrl(media),
          }
        }
        engage={{
          isLiked: Boolean(card.data.relation && card.data.relation.like),
          isClipped: Boolean(card.data.relation && card.data.relation.clip),
          likeCount: card.data.likes_count,
          commentCount: card.data.comments_count + card.data.replies_count,
          clipCount: card.data.share_count + card.data.clips_count,
        }}
        author={card.data.author}
      />
    );
  },
);

const CompactList = ({
  cards,
  handleInfinityLoad,
  loader,
  emptyElement,
  paging,
  className,
  isLoading,
  disableModal,
  trackTicket,
}: IProps) => {
  let content = emptyElement;
  let itemLength = 0;
  if (cards) {
    const filterCards = cards.filter(card => card && card.type === "card");
    itemLength = filterCards.length;
    if (itemLength > 0) {
      content = filterCards.map((card, index) => {
        return (
          <CompactCardItem
            key={card.data.id}
            card={card}
            position={index}
            trackTicket={trackTicket}
            disableModal={Boolean(disableModal)}
          />
        );
      });
    }
  }

  return (
    <div className={cx(className, styles.rootWrapper)}>
      {content ? (
        <InfiniteScroller
          itemLength={itemLength}
          loader={loader}
          paging={paging}
          threshold={150}
          isLoading={isLoading}
          loadMore={handleInfinityLoad ? handleInfinityLoad : placeholderLoad}
          className={styles.infinite}
        >
          {content}
        </InfiniteScroller>
      ) : (
        emptyElement
      )}
    </div>
  );
};

export default CompactList;
